import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Download, Printer, Search, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const PCBPaybillReportView = ({
  approvedRuns = [],
  initialRunId,
  initialDept = 'ALL',
  initialMonth = 'ALL',
  initialYear = 'ALL',
}) => {
  const { hasPermission } = useAuth();
  const [runsList, setRunsList] = useState(approvedRuns);
  const [paybillDataList, setPaybillDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const activePageRef = useRef(null);

  useEffect(() => {
    if (activePageRef.current) {
      activePageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentPage]);

  const currentDate = new Date();
  const currentMonthStr = String(currentDate.getMonth() + 1);
  const currentYearStr = String(currentDate.getFullYear());

  const [selectedDept, setSelectedDept] = useState(initialDept);
  const [fromMonth, setFromMonth] = useState(currentMonthStr);
  const [fromYear, setFromYear] = useState(currentYearStr);
  const [toMonth, setToMonth] = useState(currentMonthStr);
  const [toYear, setToYear] = useState(currentYearStr);

  const monthsList = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const yearsList = Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => String(2025 + i));

  const printRef = useRef();

  useEffect(() => {
    setSelectedDept(initialDept);
  }, [initialDept]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDept, fromMonth, fromYear, toMonth, toYear, searchTerm]);

  useEffect(() => {
    if (approvedRuns && approvedRuns.length > 0) {
      setRunsList(approvedRuns);
    } else {
      const fetchRuns = async () => {
        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setRunsList(Array.isArray(data) ? data : []);
          }
        } catch (err) {
          console.error('Failed to load fallback runs in PCBPaybillReportView:', err);
        }
      };
      fetchRuns();
    }
  }, [approvedRuns]);

  const filteredRuns = useMemo(() => {
    return runsList
      .filter((run) => {
        const runVal = Number(run.payroll_year) * 12 + Number(run.payroll_month);
        const fromVal = Number(fromYear) * 12 + Number(fromMonth);
        const toVal = Number(toYear) * 12 + Number(toMonth);
        return runVal >= fromVal && runVal <= toVal;
      })
      .sort((a, b) => {
        const aVal = Number(a.payroll_year) * 12 + Number(a.payroll_month);
        const bVal = Number(b.payroll_year) * 12 + Number(b.payroll_month);
        return aVal - bVal;
      });
  }, [runsList, fromMonth, fromYear, toMonth, toYear]);

  // Fetch detailed paybill data for all runs in parallel
  useEffect(() => {
    let active = true;
    if (filteredRuns.length > 0) {
      const load = async () => {
        setLoading(true);
        setError('');
        try {
          const promises = filteredRuns.map(async (run) => {
            const url = selectedDept && selectedDept !== 'ALL'
              ? `/api/v1/reports/pay-bill-data/${run.id}?sec_code=${encodeURIComponent(selectedDept)}`
              : `/api/v1/reports/pay-bill-data/${run.id}`;
            const response = await fetch(buildApiUrl(url), {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            if (!response.ok) {
              throw new Error(`Failed to load paybill data for run ID ${run.id}`);
            }
            return await response.json();
          });

          const results = await Promise.all(promises);
          if (active) {
            setPaybillDataList(results);
          }
        } catch (err) {
          console.error(err);
          if (active) {
            setError(err.message || 'Error loading paybill report data');
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };
      load();
    } else {
      setPaybillDataList([]);
    }
    return () => {
      active = false;
    };
  }, [filteredRuns, selectedDept]);

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (filteredRuns.length === 0) return;
    setDownloadingPdf(true);
    try {
      const url = selectedDept && selectedDept !== 'ALL'
        ? `/api/v1/reports/pay-bill-pdf/0?sec_code=${encodeURIComponent(selectedDept)}&from_month=${fromMonth}&from_year=${fromYear}&to_month=${toMonth}&to_year=${toYear}`
        : `/api/v1/reports/pay-bill-pdf/0?from_month=${fromMonth}&from_year=${fromYear}&to_month=${toMonth}&to_year=${toYear}`;

      const monthYearLabel = filteredRuns.length === 1
        ? `${fromMonth}_${fromYear}`
        : `${fromMonth}_${fromYear}_to_${toMonth}_${toYear}`;

      await downloadAuthenticatedFile(
        url,
        `Paybill_Inner_Sheets_${selectedDept}_${monthYearLabel}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF report: ' + (err.message || 'Server error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getMonthName = (month) => {
    return new Date(2025, month - 1).toLocaleString('default', { month: 'long' });
  };

  const fmt = (num) => {
    if (num === null || num === undefined) return '₹0';
    return '₹' + Math.round(Number(num)).toString();
  };

  // Plain number formatter — no ₹ symbol, used for individual field values in slip rows and label columns
  const fmtN = (num) => {
    if (num === null || num === undefined) return '0';
    return Math.round(Number(num)).toString();
  };

  // Helper: render a field with vertically-aligned colon
  const af = (label, value, w, bold = false, extra = '') => (
    <div className={`flex${bold ? ' font-bold' : ''}${extra ? ' ' + extra : ''}`}>
      <span className="inline-block" style={{ width: w, minWidth: w }}>{label}</span>
      <span>: </span>
      <span>{value}</span>
    </div>
  );


  // Helper to calculate totals for a chunk of 4 slips
  const calculateChunkTotals = (slipsChunk) => {
    const totals = {
      basic: 0, gp: 0, allowance: 0, da: 0, hra: 0, other_allow: 0,
      cca: 0, tpt: 0, net_earning: 0, recovery: 0, total_earning: 0,
      pt: 0, f_adv: 0, soc: 0, pfsub: 0, others: 0, wt: 0,
      gis: 0, it: 0, st: 0, pfadv: 0, lic: 0, bank: 0,
      vpf: 0, rd: 0, cpf: 0, l_fees: 0, total_deductions: 0, tot_cash: 0, net_pay: 0
    };

    slipsChunk.forEach((slip) => {
      const e = slip.earnings || {};
      const d = slip.deductions || {};
      totals.basic += Number(e.basic || 0);
      totals.gp += Number(e.gp || 0);
      totals.allowance += Number(e.allowance || 0);
      totals.da += Number(e.da || 0);
      totals.hra += Number(e.hra || 0);
      totals.other_allow += Number(e.other_allow || 0);
      totals.cca += Number(e.cca || 0);
      totals.tpt += Number(e.tpt || 0);
      totals.net_earning += Number(e.net_earning || 0);
      totals.recovery += Number(e.recovery || 0);
      totals.total_earning += Number(e.total_earning || 0);

      totals.pt += Number(d.pt || 0);
      totals.f_adv += Number(d.f_adv || 0);
      totals.soc += Number(d.soc || 0);
      totals.pfsub += Number(d.pfsub || 0);
      totals.others += Number(d.others || 0);
      totals.wt += Number(d.wt || 0);
      totals.gis += Number(d.gis || 0);
      totals.it += Number(d.it || 0);
      totals.st += Number(d.st || 0);
      totals.pfadv += Number(d.pfadv || 0);
      totals.lic += Number(d.lic || 0);
      totals.bank += Number(d.bank || 0);
      totals.vpf += Number(d.vpf || 0);
      totals.rd += Number(d.rd || 0);
      totals.cpf += Number(d.cpf || 0);
      totals.l_fees += Number(d.l_fees || 0);
      totals.total_deductions += Number(d.total_deductions || 0);
      totals.tot_cash += Number(d.tot_cash || 0);
      totals.net_pay += Number(d.net_pay || 0);
    });

    return totals;
  };

  // Chunk slips into pages of 4 items each, grouped by month/run
  const PAGE_SIZE = 4;
  const slipPages = useMemo(() => {
    const pages = [];

    paybillDataList.forEach((monthData) => {
      const slips = monthData.slips || [];
      const monthTotals = monthData.totals;
      const monthHeadWiseTotals = monthData.head_wise_totals;
      const monthYear = monthData.month_year;
      const printDate = monthData.print_date;
      const deptName = monthData.dept;

      const filteredSlipsForMonth = slips.filter((slip) => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;
        return (
          (slip.emp_name || '').toLowerCase().includes(q) ||
          (slip.emp_code || '').toLowerCase().includes(q) ||
          (slip.designation || '').toLowerCase().includes(q)
        );
      });

      const monthPageCount = Math.max(1, Math.ceil(filteredSlipsForMonth.length / PAGE_SIZE));

      for (let i = 0; i < filteredSlipsForMonth.length; i += PAGE_SIZE) {
        const chunk = filteredSlipsForMonth.slice(i, i + PAGE_SIZE);
        const chunkTotals = calculateChunkTotals(chunk);
        const pageIdxInMonth = Math.floor(i / PAGE_SIZE) + 1;

        pages.push({
          type: 'SLIPS',
          slips: chunk,
          totals: chunkTotals,
          pageIndexInMonth: pageIdxInMonth,
          monthPageCount: monthPageCount,
          monthYear: monthYear,
          printDate: printDate,
          deptName: deptName,
          monthTotals: monthTotals,
          monthHeadWiseTotals: monthHeadWiseTotals,
          isLastPageInMonth: pageIdxInMonth === monthPageCount
        });
      }

      // If there are no slips at all for this month (e.g. filtered out), add an empty page so it still shows the header/footer
      if (filteredSlipsForMonth.length === 0) {
        pages.push({
          type: 'SLIPS',
          slips: [],
          totals: calculateChunkTotals([]),
          pageIndexInMonth: 1,
          monthPageCount: 1,
          monthYear: monthYear,
          printDate: printDate,
          deptName: deptName,
          monthTotals: monthTotals,
          monthHeadWiseTotals: monthHeadWiseTotals,
          isLastPageInMonth: true
        });
      }

      // Add a dedicated Summary Page if there are totals
      if (monthHeadWiseTotals || monthTotals) {
        pages.push({
          type: 'SUMMARY',
          slips: [],
          totals: calculateChunkTotals([]),
          pageIndexInMonth: monthPageCount + 1,
          monthPageCount: monthPageCount + 1,
          monthYear: monthYear,
          printDate: printDate,
          deptName: deptName,
          monthTotals: monthTotals,
          monthHeadWiseTotals: monthHeadWiseTotals,
          isLastPageInMonth: false
        });
      }
    });

    // Assign global sequential pageIndex
    return pages.map((page, idx) => ({
      ...page,
      globalPageIndex: idx + 1
    }));
  }, [paybillDataList, searchTerm]);

  const totalPages = Math.max(1, slipPages.length);

  // Filter pages for interactive screen view
  const pagesToShowOnScreen = useMemo(() => {
    if (currentPage === 'ALL') return slipPages;
    const activeIdx = typeof currentPage === 'number' ? currentPage : 1;
    const pageObj = slipPages.find((p) => p.globalPageIndex === activeIdx);
    return pageObj ? [pageObj] : (slipPages.length > 0 ? [slipPages[0]] : []);
  }, [slipPages, currentPage]);

  const allEmployeesCount = useMemo(() => {
    return paybillDataList.reduce((acc, curr) => acc + (curr.slips?.length || 0), 0);
  }, [paybillDataList]);

  return (
    <div className="space-y-6">
      {/* Controls Bar - Screen Only */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="w-5 h-5 text-emerald-600 self-center" />

            {/* From Period */}
            <div className="flex items-center gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">From Month</label>
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[130px]"
                >
                  {monthsList.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">From Year</label>
                <select
                  value={fromYear}
                  onChange={(e) => setFromYear(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <span className="text-gray-400 font-bold self-end mb-2">TO</span>

            {/* To Period */}
            <div className="flex items-center gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">To Month</label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[130px]"
                >
                  {monthsList.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase">To Year</label>
                <select
                  value={toYear}
                  onChange={(e) => setToYear(e.target.value)}
                  className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end">
            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={filteredRuns.length === 0 || downloadingPdf}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors ${filteredRuns.length === 0 || downloadingPdf ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative pt-2">
          <Search className="w-4 h-4 absolute left-3 top-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search employee by name, code or designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      ) : (filteredRuns.length === 0 || paybillDataList.length === 0) ? (
        <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm text-gray-500 font-medium">
          No paybill report data available for the selected month/year period.
        </div>
      ) : (
        <div ref={printRef} className="bg-white p-6 rounded-xl border border-gray-300 shadow-sm print:p-0 print:border-none print:shadow-none font-mono text-xs text-black space-y-12">

          {/* List of Pages (4 employees per page) */}
          <div className="space-y-12">
            {pagesToShowOnScreen.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No employees found matching search.</div>
            ) : (
              pagesToShowOnScreen.map((pageObj) => {
                if (pageObj.type === 'SUMMARY') {
                  return (
                    <div key={pageObj.globalPageIndex} className="space-y-6 pb-6 page-break-after-always">
                      <div className="page-break-before-always print:pt-0 pt-2">
                        {/* Summary Page Header */}
                        <div className="pb-2 mb-4 text-sm font-bold">
                          <div className="grid grid-cols-3 items-center">
                            <div>Paybill For The Month: {pageObj.monthYear}</div>
                            <div className="text-center text-lg uppercase tracking-wider">PUNE CANTONMENT BOARD</div>
                            <div className="text-right">Dept: {pageObj.deptName}</div>
                          </div>
                          <div className="grid grid-cols-3 items-center mt-1">
                            <div></div>
                            <div className="text-center">Print Date: {pageObj.printDate}</div>
                            <div></div>
                          </div>
                        </div>

                        {/* Head-wise Totals */}
                        {pageObj.monthHeadWiseTotals && Object.entries(pageObj.monthHeadWiseTotals).map(([headName, hTot], index) => (
                          <div key={headName} className={`pt-6 ${index > 0 ? 'mt-8' : 'mt-4'} page-break-inside-avoid`}>
                            <div className="text-gray-900 font-bold text-sm mb-4">
                              Head:{headName} (Total no. Of Employees:{hTot.count || 0})
                            </div>
                            <div className="border-t border-dashed border-gray-400 mb-6"></div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                                {af('Total Basic', fmt(hTot.basic), '12ch', true)}
                                {af('Allowance', fmt(hTot.allowance), '10ch', true)}

                                {af('Total DA', fmt(hTot.da), '12ch', true)}
                                {af('Others', fmt(hTot.other_allow), '10ch', true)}

                                {af('Total HRA', fmt(hTot.hra), '12ch', true)}
                                {af('Net Sal', fmt(hTot.net_earning), '10ch', true)}

                                {af('Total CCA', fmt(hTot.cca), '12ch', true)}
                                {af('Recovery', fmt(hTot.recovery), '10ch', true)}

                                {/* {af('Total GP', fmt(hTot.gp), '12ch', true)} */}
                                {af('Tot. Err.', fmt(hTot.total_earning), '10ch', true)}

                                {af('Total TPT', fmt(hTot.tpt), '12ch', true)}
                                <div></div>
                              </div>

                              <div className="grid grid-cols-4 gap-x-2 gap-y-2">
                                {af('PT', fmt(hTot.pt), '7ch', true)}
                                {af('F.Adv', fmt(hTot.f_adv), '7ch', true)}
                                {af('Soc', fmt(hTot.soc), '7ch', true)}
                                <div className="font-bold pl-4">Total Ded: {fmt(hTot.total_deductions)}</div>

                                {af('PFSub', fmt(hTot.pfsub), '7ch', true)}
                                {af('Others', fmt(hTot.others), '7ch', true)}
                                {af('W.T.', fmt(hTot.wt), '7ch', true)}
                                <div></div>

                                {af('GIS', fmt(hTot.gis), '7ch', true)}
                                {af('IT', fmt(hTot.it), '7ch', true)}
                                {af('S.T.', fmt(hTot.st || 0), '7ch', true)}
                                <div></div>

                                {af('PF Loan', fmt(hTot.pfadv), '7ch', true)}
                                {af('LIC', fmt(hTot.lic), '7ch', true)}
                                {af('Bank', fmt(hTot.bank || 0), '7ch', true)}
                                <div></div>

                                {af('VPF', fmt(hTot.vpf), '7ch', true)}
                                {af('RD', fmt(hTot.rd), '7ch', true)}
                                {af('Cash', fmt(hTot.tot_cash), '7ch', true)}
                                <div className="font-bold pl-4">Total Sal: {fmt(hTot.net_pay)}</div>

                                {af('CPF', fmt(hTot.cpf), '7ch', true)}
                                <div></div>
                                {af('L.Fees', fmt(hTot.l_fees), '7ch', true)}
                                <div></div>
                              </div>
                            </div>
                            <div className="border-t border-dashed border-gray-400 mt-8"></div>
                          </div>
                        ))}

                        {/* Grand Total */}
                        {pageObj.monthTotals && (
                          <div className="mt-6 pt-2 page-break-inside-avoid">
                            <div className="font-bold text-sm mb-2 uppercase text-gray-900">
                              GRAND TOTAL
                            </div>
                            <div className="border-t border-dashed border-gray-400 mb-6"></div>
                            <div className="grid grid-cols-2 gap-4 pb-4">
                              <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                                {af('Total Basic', fmt(pageObj.monthTotals.basic), '12ch', true)}
                                {af('Allowance', fmt(pageObj.monthTotals.allowance), '10ch', true)}

                                {af('Total DA', fmt(pageObj.monthTotals.da), '12ch', true)}
                                {af('Others', fmt(pageObj.monthTotals.other_allow), '10ch', true)}

                                {af('Total HRA', fmt(pageObj.monthTotals.hra), '12ch', true)}
                                {af('Net Sal', fmt(pageObj.monthTotals.net_earning), '10ch', true)}

                                {af('Total CCA', fmt(pageObj.monthTotals.cca), '12ch', true)}
                                {af('Recovery', fmt(pageObj.monthTotals.recovery), '10ch', true)}

                                {/* {af('Total GP', fmt(pageObj.monthTotals.gp), '12ch', true)} */}
                                {af('Tot. Err.', fmt(pageObj.monthTotals.total_earning), '10ch', true)}

                                {af('Total TPT', fmt(pageObj.monthTotals.tpt), '12ch', true)}
                                <div></div>
                              </div>

                              <div className="grid grid-cols-4 gap-x-2 gap-y-2">
                                {af('PT', fmt(pageObj.monthTotals.pt), '7ch', true)}
                                {af('F.Adv', fmt(pageObj.monthTotals.f_adv), '7ch', true)}
                                {af('Soc', fmt(pageObj.monthTotals.soc), '7ch', true)}
                                <div className="font-bold pl-4">Total Ded: {fmt(pageObj.monthTotals.total_deductions)}</div>

                                {af('PFSub', fmt(pageObj.monthTotals.pfsub), '7ch', true)}
                                {af('Others', fmt(pageObj.monthTotals.others), '7ch', true)}
                                {af('W.T.', fmt(pageObj.monthTotals.wt), '7ch', true)}
                                <div></div>

                                {af('GIS', fmt(pageObj.monthTotals.gis), '7ch', true)}
                                {af('IT', fmt(pageObj.monthTotals.it), '7ch', true)}
                                {af('S.T.', fmt(pageObj.monthTotals.st || 0), '7ch', true)}
                                <div></div>

                                {af('PF Loan', fmt(pageObj.monthTotals.pfadv), '7ch', true)}
                                {af('LIC', fmt(pageObj.monthTotals.lic), '7ch', true)}
                                {af('Bank', fmt(pageObj.monthTotals.bank || 0), '7ch', true)}
                                <div></div>

                                {af('VPF', fmt(pageObj.monthTotals.vpf), '7ch', true)}
                                {af('RD', fmt(pageObj.monthTotals.rd), '7ch', true)}
                                {af('Cash', fmt(pageObj.monthTotals.tot_cash), '7ch', true)}
                                <div className="font-bold pl-4">Total Sal: {fmt(pageObj.monthTotals.net_pay)}</div>

                                {af('CPF', fmt(pageObj.monthTotals.cpf), '7ch', true)}
                                <div></div>
                                {af('L.Fees', fmt(pageObj.monthTotals.l_fees), '7ch', true)}
                                <div></div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Summary Page Footer */}
                        <div className="border-t border-dashed border-gray-400 pt-1 mt-2 text-center text-sm font-bold">
                          <span>Page Number: {pageObj.pageIndexInMonth}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                const pTotals = pageObj.totals;
                const isLastPage = pageObj.isLastPageInMonth;
                return (
                  <div key={pageObj.globalPageIndex} className="space-y-6 pb-6 page-break-after-always">

                    {/* Single Page Header */}
                    <div className="border-b border-dashed border-black pb-1 mb-2 text-sm font-bold">
                      <div className="grid grid-cols-3 items-center">
                        <div>Paybill For The Month: {pageObj.monthYear}</div>
                        <div className="text-center text-lg uppercase tracking-wider">PUNE CANTONMENT BOARD</div>
                        <div className="text-right">Dept: {pageObj.deptName}</div>
                      </div>
                      <div className="grid grid-cols-3 items-center mt-1">
                        <div>Earnings</div>
                        <div className="text-center">Print Date: {pageObj.printDate}</div>
                        <div className="text-right pr-24 space-x-8">
                          <span>Tot. Ded.</span>
                          <span>Salary</span>
                        </div>
                      </div>
                      <div className="text-center">Deductions</div>
                    </div>

                    {/* 4 Employees in this page */}
                    <div className="space-y-1">
                      {pageObj.slips.map((slip, index) => {
                        const e = slip.earnings || {};
                        const d = slip.deductions || {};
                        return (
                          <div key={slip.id || index} className="border-b border-dashed border-gray-400 pb-1 mb-0 page-break-inside-avoid">

                            {/* Employee Metadata Bar */}
                            <div className="font-bold text-sm mb-2 text-gray-900 pb-1 flex flex-wrap gap-y-1">
                              <span>Code: {slip.emp_code}</span>
                              <span className="ml-4">Name: {slip.emp_name}</span>
                              <span className="ml-6">Grade: {slip.designation}</span>
                              <span className="ml-6">DOB:{slip.dob}</span>
                              <span className="ml-4">DOJ:{slip.doj}</span>
                              <span className="ml-4">DOR:{slip.dor || slip.ret_date || '-'}</span>
                              <span className="ml-4">
                                Head: {slip.head_code || "Not Assigned Any Head"}
                              </span>
                              <span className="ml-6">Pay Scale: {slip.pay_scale || slip.scale || '-'}</span>
                            </div>

                            {/* 2-Column Earnings & Deductions Table */}
                            <div className="grid grid-cols-2 gap-4 my-2">

                              {/* Left Column: Earnings */}
                              <div>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                                  {af('Basic', fmtN(e.basic), '12ch')}
                                  {/* {af('Grade Pay', fmtN(e.gp), '10ch')} */}
                                  {af('Allowance', fmtN(e.allowance), '12ch')}

                                  {af('DA', fmtN(e.da), '12ch')}
                                  {af('HRA', fmtN(e.hra), '10ch')}
                                  {af('Other Allow', fmtN(e.other_allow), '12ch')}

                                  {af('CCA', fmtN(e.cca), '12ch')}
                                  {af('TPT', fmtN(e.tpt), '10ch')}
                                  {af('Net Earning', fmtN(e.net_earning), '12ch')}

                                  {af('Absent Days', slip.absent_days || 0, '12ch', false, 'mt-2')}
                                  <div className="mt-2"></div>
                                  {af('Recovery', fmtN(e.recovery), '12ch', false, 'mt-2')}

                                  <div className="col-span-2"></div>
                                  {af('Total Er.', fmtN(e.total_earning), '12ch', true, 'mt-1')}
                                </div>
                              </div>

                              {/* Right Column: Deductions */}
                              <div>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                                  {af('PT', fmtN(d.pt), '6ch')}
                                  {af('F.Adv', fmtN(d.f_adv), '7ch')}
                                  <div className="flex items-center">
                                    {af('Soc', fmtN(d.soc), '7ch')}
                                    <div className="font-bold flex gap-8 ml-8">
                                      <span style={{ minWidth: '5ch' }}>{Math.round(Number(d.total_deductions))}</span>
                                      <span>{Math.round(Number(d.net_pay || slip.net_pay || 0))}</span>
                                    </div>
                                  </div>

                                  {af('PFSub', fmtN(d.pfsub), '6ch')}
                                  {af('Others', fmtN(d.others), '7ch')}
                                  {af('W.T.', fmtN(d.wt), '7ch')}

                                  {af('GIS', fmtN(d.gis), '6ch')}
                                  {af('IT', fmtN(d.it), '7ch')}
                                  {af('Bank', fmtN(d.bank || 0), '7ch')}

                                  {af('PFAdv', fmtN(d.pfadv), '6ch')}
                                  {af('LIC', fmtN(d.lic), '7ch')}
                                  {af('CPF', fmtN(d.cpf), '7ch')}

                                  {af('VPF', fmtN(d.vpf), '6ch')}
                                  {af('RD', fmtN(d.rd), '7ch')}
                                  {af('L.Fees', fmtN(d.l_fees), '7ch')}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Page Summary Totals */}
                    <div className="mt-0 pt-0 page-break-inside-avoid">
                      <div className="grid grid-cols-2 gap-4 leading-tight">
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0">
                          {af('Total Basic', fmt(pTotals.basic), '12ch', true)}
                          {af('Allowance', fmt(pTotals.allowance), '10ch', true)}

                          {af('Total DA', fmt(pTotals.da), '12ch', true)}
                          {af('Others', fmt(pTotals.other_allow), '10ch', true)}

                          {af('Total HRA', fmt(pTotals.hra), '12ch', true)}
                          {af('Net Err.', fmt(pTotals.net_earning), '10ch', true)}

                          {af('Total CCA', fmt(pTotals.cca), '12ch', true)}
                          {af('Recovery', fmt(pTotals.recovery), '10ch', true)}

                          {af('Total GP', fmt(pTotals.gp), '12ch', true)}
                          <div></div>

                          {af('Total TPT', fmt(pTotals.tpt), '12ch', true)}
                          {af('Total Err', fmt(pTotals.total_earning), '10ch', true)}
                        </div>

                        <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                          {af('PT', fmt(pTotals.pt), '7ch', true)}
                          {af('F.Adv', fmt(pTotals.f_adv), '7ch', true)}
                          {af('Soc', fmt(pTotals.soc), '7ch', true)}
                          <div className="font-bold pl-4">Tot. Ded : {fmt(pTotals.total_deductions)}</div>

                          {af('PFSub.', fmt(pTotals.pfsub), '7ch', true)}
                          {af('Others', fmt(pTotals.others), '7ch', true)}
                          {af('W.T.', fmt(pTotals.wt), '7ch', true)}
                          <div></div>

                          {af('GIS', fmt(pTotals.gis), '7ch', true)}
                          {af('IT', fmt(pTotals.it), '7ch', true)}
                          {af('S.T.', fmt(pTotals.st || 0), '7ch', true)}
                          <div></div>

                          {af('PF Loan', fmt(pTotals.pfadv), '7ch', true)}
                          {af('LIC', fmt(pTotals.lic), '7ch', true)}
                          {af('Bank', fmt(pTotals.bank || 0), '7ch', true)}
                          <div className="font-bold pl-4">Tot. Cash: {fmt(pTotals.tot_cash)}</div>

                          {af('VPF', fmt(pTotals.vpf), '7ch', true)}
                          {af('RD', fmt(pTotals.rd), '7ch', true)}
                          {af('CPF', fmt(pTotals.cpf), '7ch', true)}
                          <div className="font-bold pl-4">Tot. Sal : {fmt(pTotals.net_pay)}</div>

                          <div></div>
                          <div></div>
                          {af('L.Fees', fmt(pTotals.l_fees), '7ch', true)}
                          <div></div>
                        </div>
                      </div>
                    </div>

                    {/* Standard Footer for current page */}
                    <div className="border-t border-dashed border-black pt-1 mt-1 text-center text-sm font-bold">
                      <span>Page Number: {pageObj.pageIndexInMonth}</span>
                    </div>

                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Pagination Bar - Screen Only */}
      {!loading && !error && totalPages > 1 && (
        <div className="print:hidden flex flex-col sm:flex-row items-center justify-between bg-gray-50 px-6 py-4 rounded-xl border border-gray-200 gap-4 max-w-full">
          <div className="text-sm font-semibold text-gray-500 shrink-0">
            {currentPage === 'ALL' ? (
              <span>Showing <strong>All Pages</strong> ({allEmployeesCount} Employees)</span>
            ) : (
              <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({allEmployeesCount} Employees total, 4 per page)</span>
            )}
          </div>

          <div className="w-full max-w-full overflow-x-auto py-1">
            <div className="flex w-max min-w-max items-center space-x-1.5 ml-auto">
              {/* Previous Button */}
              <button
                onClick={() => typeof currentPage === 'number' && setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || currentPage === 'ALL'}
                className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                Prev
              </button>

              {/* Page Number Boxes */}
              {slipPages.map((p) => {
                const isActive = currentPage === p.globalPageIndex;
                return (
                  <button
                    key={p.globalPageIndex}
                    ref={isActive ? activePageRef : null}
                    onClick={() => setCurrentPage(p.globalPageIndex)}
                    className={`min-w-[34px] h-8 px-2 text-xs font-bold rounded-lg border transition-all shrink-0 ${isActive
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-md scale-105'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                      }`}
                  >
                    {p.globalPageIndex}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                onClick={() => typeof currentPage === 'number' && setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || currentPage === 'ALL'}
                className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                Next
              </button>

              {/* View All Pages Button */}
              <button
                onClick={() => setCurrentPage(currentPage === 'ALL' ? 1 : 'ALL')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ml-2 shrink-0 ${currentPage === 'ALL'
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {currentPage === 'ALL' ? 'Single Page' : 'View All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Stylesheet */}
      <style>{`
        @media print {
          @page {
            size: legal portrait;
            margin: 10mm;
          }
          body * {
            visibility: hidden;
          }
          #root {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .font-mono {
            font-family: 'Courier New', Courier, monospace !important;
          }
          div[ref="printRef"], div[ref="printRef"] * {
            visibility: visible;
          }
          div[ref="printRef"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 10mm;
          }
          .page-break-after-always {
            page-break-after: always;
          }
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export const PCBWageSlipView = ({
  approvedRuns = [],
  initialRunId,
  initialDept = 'ALL',
  initialMonth = 'ALL',
  initialYear = 'ALL',
}) => {
  const { hasPermission } = useAuth();
  const [runsList, setRunsList] = useState(approvedRuns);
  const [paybillDataList, setPaybillDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmpCode, setSelectedEmpCode] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);

  const currentDate = new Date();
  const currentMonthStr = initialMonth !== 'ALL' && initialMonth ? String(initialMonth) : String(currentDate.getMonth() + 1);
  const currentYearStr = initialYear !== 'ALL' && initialYear ? String(initialYear) : String(currentDate.getFullYear());

  const [selectedDept, setSelectedDept] = useState(initialDept);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [selectedYear, setSelectedYear] = useState(currentYearStr);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const monthsList = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const yearsList = Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => String(2025 + i));

  const printRef = useRef();

  useEffect(() => {
    setSelectedDept(initialDept);
  }, [initialDept]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDept, selectedMonth, selectedYear, searchTerm, selectedEmpCode]);

  useEffect(() => {
    if (approvedRuns && approvedRuns.length > 0) {
      setRunsList(approvedRuns);
    } else {
      const fetchRuns = async () => {
        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setRunsList(Array.isArray(data) ? data : []);
          }
        } catch (err) {
          console.error('Failed to load fallback runs in PCBWageSlipView:', err);
        }
      };
      fetchRuns();
    }
  }, [approvedRuns]);

  const filteredRuns = useMemo(() => {
    return runsList.filter((run) => {
      return Number(run.payroll_month) === Number(selectedMonth) && Number(run.payroll_year) === Number(selectedYear);
    });
  }, [runsList, selectedMonth, selectedYear]);

  // Fetch detailed paybill data for matching run
  useEffect(() => {
    let active = true;
    if (filteredRuns.length > 0) {
      const load = async () => {
        setLoading(true);
        setError('');
        try {
          const promises = filteredRuns.map(async (run) => {
            const url = selectedDept && selectedDept !== 'ALL'
              ? `/api/v1/reports/pay-bill-data/${run.id}?sec_code=${encodeURIComponent(selectedDept)}`
              : `/api/v1/reports/pay-bill-data/${run.id}`;
            const response = await fetch(buildApiUrl(url), {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            if (!response.ok) {
              throw new Error(`Failed to load paybill data for run ID ${run.id}`);
            }
            return await response.json();
          });

          const results = await Promise.all(promises);
          if (active) {
            setPaybillDataList(results);
          }
        } catch (err) {
          console.error(err);
          if (active) {
            setError(err.message || 'Error loading wage slip data');
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };
      load();
    } else {
      setPaybillDataList([]);
    }
    return () => {
      active = false;
    };
  }, [filteredRuns, selectedDept]);

  const fmt = (num) => {
    if (num === null || num === undefined || num === '') return '0';
    const val = Number(num);
    if (isNaN(val)) return '0';
    return Math.round(val).toString();
  };

  // Extract all unique employees across fetched runs for dropdown
  const allEmployeesList = useMemo(() => {
    const map = new Map();
    paybillDataList.forEach((monthData) => {
      (monthData.slips || []).forEach((slip) => {
        if (slip.emp_code && !map.has(slip.emp_code)) {
          map.set(slip.emp_code, {
            emp_code: slip.emp_code,
            emp_name: slip.emp_name,
            designation: slip.designation,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [paybillDataList]);

  // Group all slips chronologically
  const wageSlips = useMemo(() => {
    const slips = [];
    paybillDataList.forEach((monthData) => {
      const monthYear = monthData.month_year;
      const deptName = monthData.dept;
      (monthData.slips || []).forEach((slip) => {
        const q = searchTerm.trim().toLowerCase();
        const matchesSearch = !q || (
          (slip.emp_name || '').toLowerCase().includes(q) ||
          (slip.emp_code || '').toLowerCase().includes(q) ||
          (slip.designation || '').toLowerCase().includes(q)
        );
        const matchesEmp = selectedEmpCode === 'ALL' || slip.emp_code === selectedEmpCode;

        if (matchesSearch && matchesEmp) {
          slips.push({
            ...slip,
            monthYear,
            deptName,
          });
        }
      });
    });
    return slips;
  }, [paybillDataList, searchTerm, selectedEmpCode]);

  const totalPages = Math.max(1, wageSlips.length);

  const currentSlip = useMemo(() => {
    if (wageSlips.length === 0) return null;
    const idx = Math.min(Math.max(0, currentPage - 1), wageSlips.length - 1);
    return wageSlips[idx];
  }, [wageSlips, currentPage]);

  const handleDownloadPdf = async () => {
    if (wageSlips.length === 0) return;
    setDownloadingPdf(true);
    try {
      let url = `/api/v1/reports/wage-slip-pdf?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedDept && selectedDept !== 'ALL') {
        url += `&sec_code=${encodeURIComponent(selectedDept)}`;
      }
      if (selectedEmpCode && selectedEmpCode !== 'ALL') {
        url += `&emp_code=${encodeURIComponent(selectedEmpCode)}`;
      }

      const filename = selectedEmpCode && selectedEmpCode !== 'ALL'
        ? `Wage_Slip_${selectedMonth}_${selectedYear}_${selectedEmpCode}.pdf`
        : `Wage_Slips_${selectedMonth}_${selectedYear}_All.pdf`;

      await downloadAuthenticatedFile(url, filename);
    } catch (err) {
      console.error('Error downloading Wage Slip PDF:', err);
      alert('Failed to download Wage Slip PDF report: ' + (err.message || 'Server error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls Bar - Screen Only */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-4">
            <Calendar className="w-5 h-5 text-teal-600 self-center" />

            {/* Month Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">MONTH</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white min-w-[130px]"
              >
                {monthsList.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">YEAR</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white min-w-[100px]"
              >
                {yearsList.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase">EMPLOYEE</label>
              <select
                value={selectedEmpCode}
                onChange={(e) => setSelectedEmpCode(e.target.value)}
                className="mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white min-w-[220px]"
              >
                <option value="ALL">All Employees ({allEmployeesList.length})</option>
                {allEmployeesList.map((emp) => (
                  <option key={emp.emp_code} value={emp.emp_code}>
                    {emp.emp_code} - {emp.emp_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end">
            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={wageSlips.length === 0 || downloadingPdf}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors ${wageSlips.length === 0 || downloadingPdf ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative pt-2">
          <Search className="w-4 h-4 absolute left-3 top-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search employee by name, code or designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none bg-white"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      ) : (filteredRuns.length === 0 || paybillDataList.length === 0 || wageSlips.length === 0) ? (
        <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm text-gray-500 font-medium">
          No wage slip data available for the selected month/year period or employee filter.
        </div>
      ) : currentSlip ? (
        <div ref={printRef} className="bg-white p-2 sm:p-6 rounded-xl border border-gray-200 shadow-sm print:p-0 print:border-none print:shadow-none text-black font-mono">

          {/* Wage Slip Under M. W. Act Document Container */}
          <div className="border border-black p-0 text-xs font-mono leading-tight bg-white max-w-[850px] mx-auto overflow-x-auto">
            <div className="min-w-[650px]">
              {(() => {
                const e = currentSlip.earnings || {};
                const d = currentSlip.deductions || {};

                const earningsRows = [
                  { label: 'Basic Pay :', value: fmt(e.basic) },
                  { label: 'Allowance :', value: fmt(e.allowance) },
                  { label: 'D.A. :', value: fmt(e.da) },
                  { label: 'H.R.A. :', value: fmt(e.hra) },
                  { label: 'C.C.A. :', value: fmt(e.cca) },
                  { label: 'TPT Allow :', value: fmt(e.tpt) },
                  { label: 'Others :', value: fmt(e.other_allow) },
                  { label: 'Pay Level', value: currentSlip.pay_scale || currentSlip.scale || 'S-7' },
                  { label: '', value: '' },
                ];

                const ded1Rows = [
                  { label: 'P.F.Subs :', value: fmt(d.pfsub) },
                  { label: 'P.F. Advance :', value: fmt(d.pfadv) },
                  { label: 'VPF :', value: fmt(d.vpf) },
                  { label: 'LIC :', value: fmt(d.lic) },
                  { label: 'GIS :', value: fmt(d.gis) },
                  { label: 'F.A. :', value: fmt(d.f_adv) },
                  { label: 'Prof Tax :', value: fmt(d.pt) },
                  { label: 'Absent rec :', value: fmt(currentSlip.absent_days || currentSlip.leave_deduction || 0) },
                  { label: '', value: '' },
                ];

                const ded2Rows = [
                  { label: 'Income Tax :', value: fmt(d.it) },
                  { label: 'Bank Loan :', value: fmt(d.bank || 0) },
                  { label: 'Society Loan :', value: fmt(d.soc) },
                  { label: 'Sanitary Cess :', value: fmt(d.st || 0) },
                  { label: 'Water Cess :', value: fmt(d.wt) },
                  { label: 'R.D. :', value: fmt(d.rd) },
                  { label: 'Licence Fee :', value: fmt(d.l_fees) },
                  { label: 'Others :', value: fmt(d.others) },
                  { label: 'Recovery :', value: fmt((currentSlip.earnings || {}).recovery || 0) },
                ];

                const dor = (() => {
                  if (currentSlip.ret_date && currentSlip.ret_date !== '-') return currentSlip.ret_date;
                  if (currentSlip.dob && currentSlip.dob !== '-') {
                    const parts = currentSlip.dob.split('-');
                    if (parts.length === 3) {
                      let day = parseInt(parts[0]);
                      let month = parseInt(parts[1]);
                      let year = parseInt(parts[2]);
                      if (!isNaN(year) && !isNaN(month)) {
                        let retYear = year + 60;
                        let retMonth = month;
                        if (day === 1) {
                          retMonth = month === 1 ? 12 : month - 1;
                          if (retMonth === 12) retYear -= 1;
                        }
                        const lastDay = new Date(retYear, retMonth, 0).getDate();
                        const lastDayStr = String(lastDay).padStart(2, '0');
                        const retMonthStr = String(retMonth).padStart(2, '0');
                        return `${lastDayStr}-${retMonthStr}-${retYear}`;
                      }
                    }
                  }
                  return '-';
                })();

                const desig = currentSlip.designation ? currentSlip.designation.split('/')[0].trim() : '';
                const dept = currentSlip.department && currentSlip.department !== '-' ? currentSlip.department : '';
                const desigDept = dept ? `${desig} / ${dept}` : desig;

                return (
                  <table className="w-full border-collapse text-xs font-mono font-normal">
                    <tbody>
                      {/* Header Row 1: Logo (Col 1, rowSpan 2) + Title (Col 2-6, colSpan 5) */}
                      <tr>
                        <td rowSpan={2} className="border-r border-b border-black w-[22%] p-1 text-center align-middle bg-white">
                          <img
                            src="/pcb-logo.png"
                            alt="Pune Cantonment Board Logo"
                            className="max-h-12 max-w-[70px] object-contain mx-auto"
                          />
                        </td>
                        <td colSpan={5} className="border-b border-black py-1 text-center">
                          <h1 className="text-sm sm:text-base font-bold uppercase tracking-wider text-black m-0">
                            PUNE CANTONMENT BOARD
                          </h1>
                        </td>
                      </tr>

                      {/* Header Row 2: Subtitle (Col 2-6, colSpan 5) */}
                      <tr>
                        <td colSpan={5} className="border-b border-black py-0.5 text-center">
                          <h2 className="text-xs sm:text-sm font-bold text-black m-0">
                            Wage Slip Under M. W. Act
                          </h2>
                        </td>
                      </tr>

                      {/* Info Row 1: Employee Code (Col 1 & 2) + Designation & Dept. (Col 3-4 label, Col 5-6 value) */}
                      <tr className="border-b border-black font-normal">
                        <td className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[22%]">
                          Employee Code&nbsp;&nbsp; :
                        </td>
                        <td className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[14%]">
                          {currentSlip.emp_code}
                        </td>
                        <td colSpan={2} className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[32%]">
                          Designation &amp; Dept.&nbsp; :
                        </td>
                        <td colSpan={2} className="px-2 py-0.5 whitespace-nowrap w-[32%]">
                          {desigDept}
                        </td>
                      </tr>

                      {/* Info Row 2: Salary for Month (Col 1 & 2) + DOB (Col 3-4) + Date of Ret (Col 5-6) */}
                      <tr className="border-b border-black font-normal">
                        <td className="border-r border-black px-2 py-0.5 whitespace-nowrap">
                          Salary for the Month of&nbsp; :
                        </td>
                        <td className="border-r border-black px-2 py-0.5 whitespace-nowrap">
                          {currentSlip.monthYear}
                        </td>
                        <td colSpan={2} className="border-r border-black px-2 py-0.5 whitespace-nowrap">
                          Date of Birth: {currentSlip.dob || '-'}
                        </td>
                        <td colSpan={2} className="px-2 py-0.5 whitespace-nowrap">
                          Date of Ret. : {dor}
                        </td>
                      </tr>

                      {/* Info Row 3: Name across all 6 columns */}
                      <tr className="border-b border-black font-normal">
                        <td colSpan={6} className="px-2 py-0.5 whitespace-nowrap">
                          <span>Name :&nbsp;&nbsp;</span>
                          <span className="uppercase">{currentSlip.emp_name}</span>
                        </td>
                      </tr>

                      {/* Table Header: EARNINGS Rs. & DEDUCTION Rs. */}
                      <tr>
                        <th colSpan={2} className="border-r border-b border-black py-1 px-2 font-normal text-center">
                          EARNINGS Rs.
                        </th>
                        <th colSpan={4} className="border-b border-black py-1 px-2 font-normal text-center">
                          DEDUCTION Rs.
                        </th>
                      </tr>

                      {/* 9 Item Rows */}
                      {earningsRows.map((item, idx) => (
                        <tr key={idx} className="border-b border-black font-normal">
                          {/* Earnings Item */}
                          <td className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[22%]">
                            {item.label}
                          </td>
                          {/* Earnings Amount */}
                          <td className="border-r border-black px-2 py-0.5 text-right whitespace-nowrap w-[14%]">
                            {item.value}
                          </td>

                          {/* Ded 1 Item */}
                          <td className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[19%]">
                            {ded1Rows[idx].label}
                          </td>
                          {/* Ded 1 Amount */}
                          <td className="border-r border-black px-2 py-0.5 text-right whitespace-nowrap w-[13%]">
                            {ded1Rows[idx].value}
                          </td>

                          {/* Ded 2 Item */}
                          <td className="border-r border-black px-2 py-0.5 whitespace-nowrap w-[19%]">
                            {ded2Rows[idx].label}
                          </td>
                          {/* Ded 2 Amount */}
                          <td className="px-2 py-0.5 text-right whitespace-nowrap w-[13%]">
                            {ded2Rows[idx].value}
                          </td>
                        </tr>
                      ))}

                      {/* Totals Row */}
                      <tr className="border-b border-black font-normal">
                        <td colSpan={2} className="border-r border-black px-2 py-1">
                          <div className="flex justify-between items-center">
                            <span>Total Earnings:</span>
                            <span>{fmt(e.total_earning)}</span>
                          </div>
                        </td>
                        <td colSpan={2} className="border-r border-black px-2 py-1">
                          <div className="flex justify-between items-center">
                            <span>Total Deduction :</span>
                            <span>{fmt(d.total_deductions)}</span>
                          </div>
                        </td>
                        <td colSpan={2} className="px-2 py-1">
                          &nbsp;
                        </td>
                      </tr>

                      {/* Net Salary & Signature Row */}
                      <tr className="border-b border-black font-normal">
                        <td colSpan={4} className="border-r border-black px-2 py-2 align-middle">
                          <span>Total Salary Rs:</span>
                          <span className="ml-1">{fmt(d.net_pay)}</span>
                        </td>
                        <td colSpan={2} className="px-2 py-1 text-center align-bottom h-14">
                          <div className="pt-4">
                            <span>Pay-in-charge</span>
                          </div>
                        </td>
                      </tr>

                      {/* Footnote 1 */}
                      <tr className="border-b border-black font-normal">
                        <td colSpan={6} className="px-2 py-1 text-[11px] leading-tight text-black">
                          * If there is any error or discrepancy, please bring to the notice of Accounts Branch within seven days.
                        </td>
                      </tr>

                      {/* Footnote 2 */}
                      <tr className="font-normal">
                        <td colSpan={6} className="px-2 py-1 text-[11px] leading-tight text-black">
                          * This office is not responsible for any recovery of bank loans.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}

      {/* Pagination Bar - Screen Only */}
      {!loading && !error && totalPages > 1 && (
        <div className="print:hidden flex flex-wrap items-center justify-between bg-gray-50 px-6 py-4 rounded-xl border border-gray-200 gap-4">
          <div className="text-sm font-semibold text-gray-500">
            Showing Wage Slip <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({wageSlips.length} Slips total)
          </div>

          <div className="flex items-center space-x-2 ml-auto">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <span className="text-xs font-bold text-gray-700 px-2">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Print Stylesheet */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #root {
            padding: 0 !important;
            margin: 0 !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .font-mono {
            font-family: 'Courier New', Courier, monospace !important;
          }
          div[ref="printRef"], div[ref="printRef"] * {
            visibility: visible;
          }
          div[ref="printRef"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 5mm;
          }
        }
      `}</style>
    </div>
  );
};

export default PCBPaybillReportView;
